import React from 'react';
import { 
  Search, 
  Sparkles, 
  Zap, 
  Video, 
  FileText, 
  TrendingUp, 
  Plus, 
  ChevronRight, 
  BarChart3, 
  MessageSquare,
  Image as ImageIcon,
  Mic,
  Brain,
  Share2,
  BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  BarChart, 
  Bar,
  Cell
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const usageData = [
  { name: 'Mon', usage: 45 },
  { name: 'Tue', usage: 52 },
  { name: 'Wed', usage: 38 },
  { name: 'Thu', usage: 65 },
  { name: 'Fri', usage: 48 },
  { name: 'Sat', usage: 72 },
  { name: 'Sun', usage: 58 },
];

const categoryData = [
  { name: '效率', value: 35, color: 'hsl(var(--primary))' },
  { name: '创作', value: 25, color: 'hsl(var(--secondary))' },
  { name: '学习', value: 20, color: 'hsl(var(--accent))' },
  { name: '办公', value: 20, color: 'hsl(var(--info))' },
];

const features = [
  { 
    id: 'transcription', 
    title: '实时字幕转写', 
    desc: '音视频上传/实时语音转文字', 
    icon: <Mic className="h-6 w-6" />,
    category: '效率提效',
    tier: 'paid',
    path: '/ai-hub/transcription'
  },
  { 
    id: 'batch', 
    title: '批量内容处理', 
    desc: '批量图文生成、文档总结', 
    icon: <Zap className="h-6 w-6" />,
    category: '效率提效',
    tier: 'paid',
    path: '/ai-hub/copy-polishing'
  },
  { 
    id: 'video', 
    title: '视频剪辑辅助', 
    desc: '脚本分镜、字幕生成', 
    icon: <Video className="h-6 w-6" />,
    category: '内容创作',
    tier: 'beta',
    path: '/ai-hub/copy-polishing'
  },
  { 
    id: 'originality', 
    title: '原创检测优化', 
    desc: '内容检测与建议优化', 
    icon: <FileText className="h-6 w-6" />,
    category: '内容创作',
    tier: 'paid',
    path: '/ai-hub/copy-polishing'
  },
  { 
    id: 'mindmap', 
    title: 'AI思维导图', 
    desc: '一键生成结构化图表', 
    icon: <Brain className="h-6 w-6" />,
    category: '办公工具',
    tier: 'paid',
    path: '/ai-hub/copy-polishing'
  },
  { 
    id: 'mistake', 
    title: 'AI错题本', 
    desc: '拍照识别、自动解析', 
    icon: <BookOpen className="h-6 w-6" />,
    category: '学习实用',
    tier: 'paid',
    path: '/ai-hub/copy-polishing'
  }
];

const AIHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const username = profile?.username || '创作者';
  const isBeta = profile?.membership_tier === 'beta';

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      {/* 顶部页眉 */}
      <div className="tech-flow-bg h-[200px] md:h-[260px] flex flex-col justify-center px-6 md:px-12">
        <div className="container mx-auto">
          <div className="flex items-center gap-2 text-white/80 mb-2">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">万象聚聚呀 3.0</span>
          </div>
          <h1 className="text-desktop-h1 max-sm:text-mobile-h1 text-white mb-4">
            早安，{username} <span className="logo-glow">{isBeta && ' (Beta)'}</span>
          </h1>
          <p className="text-white/70 max-w-xl">
            欢迎回到 AI 助手。今日已为您节省约 1.5 小时创作时间，继续保持高效！
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 -mt-10">
        {/* 快捷入口与统计概览 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* 使用统计 */}
          <Card className="lg:col-span-2 rounded-xl shadow-card overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">AI 创作力趋势</CardTitle>
                <CardDescription>过去 7 天的创作效率统计</CardDescription>
              </div>
              <TrendingUp className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent className="h-[200px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={usageData}>
                  <defs>
                    <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: 'hsl(var(--muted-foreground))'}} />
                  <YAxis hide />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="usage" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorUsage)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* 快捷搜索 */}
          <div className="flex flex-col gap-6">
            <Card className="rounded-xl shadow-card border-primary/20 bg-primary/5 flex-1">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  快速指令
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Input 
                    placeholder="输入指令，如：总结这篇PDF..." 
                    className="rounded-lg border-primary/20 bg-background/50 pl-10 h-12"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-white transition-colors">文案润色</Badge>
                  <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-white transition-colors">生成思维导图</Badge>
                  <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-white transition-colors">PPT大纲</Badge>
                  <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-white transition-colors">视频脚本</Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card className="rounded-xl shadow-neon border-secondary/20 bg-secondary/5">
              <CardHeader className="py-4">
                <CardTitle className="text-sm uppercase tracking-wider text-secondary flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  会员专属功能
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-sm mb-4">您的 Beta 权益已激活。快去体验最新的 AI 视频创作工具吧！</p>
                <Button 
                  variant="secondary" 
                  className="w-full bg-secondary text-white hover:bg-secondary/90 shadow-md"
                  onClick={() => navigate('/membership')}
                >
                  进入 Pro Hub
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 功能导航 */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-desktop-h2 max-sm:text-mobile-h2">功能聚合</h2>
            <Button 
              variant="ghost" 
              className="text-primary gap-1"
              onClick={() => {}}
            >
              查看全部 <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.id} className="group rounded-xl card-hover border-border/50 hover:border-primary/30">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={
                      `w-12 h-12 rounded-xl flex items-center justify-center transition-colors 
                      ${feature.tier === 'beta' ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'}
                      group-hover:bg-primary group-hover:text-white`
                    }>
                      {feature.icon}
                    </div>
                    {feature.tier === 'beta' ? (
                      <Badge className="bg-accent-oss text-white border-none">Beta</Badge>
                    ) : (
                      <Badge variant="outline" className="border-accent-paid text-accent-paid">PRO</Badge>
                    )}
                  </div>
                  <h3 className="font-bold text-lg mb-1 group-hover:text-primary transition-colors">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{feature.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{feature.category}</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 px-2 group-hover:bg-primary/10 group-hover:text-primary"
                      onClick={() => navigate(feature.path || '/ai-hub')}
                    >
                      立即开启
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* 底部功能区 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="rounded-xl overflow-hidden glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-accent" />
                分类使用分布
              </CardTitle>
            </CardHeader>
            <CardContent className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fontSize: 12}} />
                  <Tooltip cursor={{fill: 'transparent'}} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-xl overflow-hidden bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                社区热门讨论
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  "如何用 AI 辅助制作高转化率的视频脚本？",
                  "3.0 版本的新音色感觉非常真实，推荐使用！",
                  "求一个自媒体运营的数据分析模板..."
                ].map((topic, i) => (
                  <div key={topic} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/50 cursor-pointer transition-colors border border-transparent hover:border-border">
                    <span className="text-sm font-medium line-clamp-1">{topic}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-primary/20 text-primary"
                onClick={() => {}}
              >
                进入交流社区
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 悬浮助手 */}
      <Button 
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary shadow-float hover:scale-110 transition-transform p-0 flex items-center justify-center"
        onClick={() => navigate('/ai-hub')}
      >
        <Sparkles className="h-6 w-6 text-white" />
      </Button>
    </div>
  );
};

export default AIHubPage;
